import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

export const Reveal = ({ children, delay = 0, className = "", y = 28, ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.2, 0.8, 0.2, 1] }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const Lines = ({ lines, className = "", delay = 0, stagger = 0.12 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
        <motion.span
          className="block"
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1, delay: delay + i * stagger, ease: [0.2, 0.8, 0.2, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

export const SectionHead = ({ eyebrow, title, desc, dark = false, align = "left", className = "" }) => (
  <Reveal className={`${align === "center" ? "text-center mx-auto" : ""} max-w-3xl ${className}`}>
    <p className={`eyebrow mb-4 ${dark ? "!text-violet-300" : ""}`}>{eyebrow}</p>
    <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] ${dark ? "text-white" : "text-[var(--qd-ink)]"}`}>{title}</h2>
    {desc && <p className={`mt-5 text-base md:text-lg leading-relaxed ${dark ? "text-white/70" : "text-[var(--qd-muted)]"}`}>{desc}</p>}
  </Reveal>
);

export const ArrowLink = ({ children, onClick, href, testId, className = "" }) => {
  const cls = `link-arrow text-sm ${className}`;
  if (href?.startsWith("/")) return <Link to={href} onClick={onClick} className={cls} data-testid={testId}>{children} <ArrowRight className="w-4 h-4" /></Link>;
  const Tag = href ? "a" : "button";
  return (
    <Tag href={href} onClick={onClick} className={`link-arrow text-sm ${className}`} data-testid={testId}>
      {children} <ArrowRight className="w-4 h-4" />
    </Tag>
  );
};

export const CheckItem = ({ children, dark = false }) => (
  <li className="flex gap-3 items-start">
    <span className={`mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${dark ? "bg-white/15 text-white" : "bg-[var(--qd-tint)] text-[var(--qd-accent)]"}`}>
      <Check className="w-3 h-3" strokeWidth={3} />
    </span>
    <span className={`text-sm leading-relaxed ${dark ? "text-white/80" : "text-[var(--qd-ink)]"}`}>{children}</span>
  </li>
);

export const Logo = ({ dark = false, size = 32 }) => (
  <span className="inline-flex items-center gap-2.5" data-testid="logo">
    <img src="/logo.png" alt="" width={size} height={size} className="object-contain" aria-hidden />
    <span className={`font-display text-lg tracking-tight whitespace-nowrap ${dark ? "text-white" : "text-[var(--qd-ink)]"}`}>Quantum Desk</span>
  </span>
);

export const Spot = ({ name, className = "", size = 160, alt = "" }) => (
  <motion.img
    src={`/spots/${name}.webp`}
    alt={alt}
    aria-hidden={!alt}
    width={size}
    height={Math.round(size * 0.72)}
    loading="lazy"
    decoding="async"
    initial={{ opacity: 0, y: 12, rotate: -1 }}
    whileInView={{ opacity: 1, y: 0, rotate: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
    className={`pointer-events-none select-none object-cover rounded-2xl border-4 border-white shadow-[0_24px_50px_-24px_rgba(80,0,255,0.45)] ${className}`}
    data-testid={`spot-${name}`}
  />
);

export const BrandLogo = ({ src, name, size = 20, className = "" }) => (
  <img src={src} alt={`${name} logo`} width={size} height={size} loading="lazy" decoding="async" className={`object-contain ${className}`} style={{ width: size, height: size }} />
);

export const Tile = ({ logo, logo2, code, name, desc, selected, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    data-testid={testId}
    className={`card-lift text-left rounded-2xl p-4 border transition-colors w-full h-full flex flex-col ${selected ? "bg-[var(--qd-accent)] text-white border-[var(--qd-accent)]" : "glass border-transparent"}`}
  >
    <span className={`inline-flex self-start h-10 items-center gap-1.5 rounded-xl px-2 ${selected ? "bg-white" : "bg-white border border-[var(--qd-line)]"}`}>
      {logo ? <BrandLogo src={logo} name={name} size={22} /> : <span className="font-mono text-base font-semibold text-[var(--qd-accent)] px-1">{code}</span>}
      {logo2 && <BrandLogo src={logo2} name="Outlook" size={22} />}
    </span>
    <p className="mt-3 font-semibold text-sm">{name}</p>
    <p className={`text-xs mt-0.5 ${selected ? "text-white/70" : "text-[var(--qd-muted)]"}`}>{desc}</p>
  </button>
);
